/**
 * The first rows of a CSV, read in the browser so the operator can say which
 * column is which before anything is sent (plan §G.5).
 *
 * DISPLAY ONLY. The server reads the whole file with its own reader
 * (`apps/api/src/bank-import/csv.ts`) and that reading is the one imported;
 * the preview it returns shows the rows as the server read them, which is
 * where a mistake in this one would show. This exists only so the column
 * choices can be offered by name.
 *
 * RFC 4180: quoted fields, doubled quotes, commas and line breaks inside
 * quotes, a byte-order mark, and CRLF.
 */

export function firstCsvRows(text: string, limit = 6): string[][] {
  const source = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < source.length && rows.length < limit; i += 1) {
    const ch = source[i]!;
    if (quoted) {
      if (ch === '"' && source[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && source[i + 1] === '\n') i += 1;
      row.push(cell);
      if (row.some((value) => value.trim() !== '')) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (rows.length < limit && (cell !== '' || row.length > 0)) {
    row.push(cell);
    if (row.some((value) => value.trim() !== '')) rows.push(row);
  }
  return rows;
}

/** A name for each column: the header row's text, or Column 1, Column 2, … */
export function columnNames(rows: string[][], hasHeaderRow: boolean): string[] {
  const width = rows.reduce((most, row) => Math.max(most, row.length), 0);
  return Array.from({ length: width }, (_, index) => {
    const named = hasHeaderRow ? rows[0]?.[index]?.trim() : '';
    return named ? `${named} (Column ${index + 1})` : `Column ${index + 1}`;
  });
}

/**
 * A first guess at the mapping from the header's names, which the operator
 * can change. Only a guess: nothing is imported without the preview.
 */
export function guessColumns(header: string[]): {
  date: number | null;
  description: number | null;
  amount: number | null;
  out: number | null;
  into: number | null;
} {
  const find = (pattern: RegExp) => {
    const index = header.findIndex((name) => pattern.test(name.trim()));
    return index === -1 ? null : index;
  };
  return {
    date: find(/date/i),
    description: find(/description|payee|name|memo|details/i),
    amount: find(/^amount$|^amt$/i),
    out: find(/debit|withdrawal|money out|paid out/i),
    into: find(/credit|deposit|money in|paid in/i),
  };
}
